// Doctor portal API client.
//
// Calls go through the generated client's `request()` — never `.instance` —
// because that is where the securityWorker attaches the bearer token. An
// `.instance.get(...)` is sent unauthenticated and the backend answers 401.
// (Same trap the inventory client documents.)
//
// These endpoints are hand-wired rather than generated: they exist on the branch
// but not yet on dev, so `npm run generate-api` cannot see them. Once the backend
// is deployed, regenerate and replace these `request()` calls with the generated
// methods — the shapes below are the OpenAPI shapes, so that swap is mechanical.

import { ApiClient } from '@api/api-configuration';
import { ContentType } from '@api/READ_ONLY/doctors_api/Api';
import type { VisitPopulated, VisitsGetAllPaginationResponse } from '@api/READ_ONLY/visits_api/Api';

import type {
  DoctorPatient,
  DoctorInstruction,
  DoctorInstructionCreate,
  DoctorInstructionStatus,
  DoctorThreshold,
  DoctorThresholdUpsert,
  MyDoctorRecord,
  Paginated,
  ThresholdBreach,
} from './types';

const doctors = () => ApiClient.doctorsApi;

export const getMyDoctorRecord = () =>
  doctors()
    .request<MyDoctorRecord>({ path: '/doctor/me', method: 'GET', format: 'json' })
    .then((r) => r.data);

/**
 * The doctor's roster.
 *
 * `includeEnded` widens it to patients whose every link to this doctor is
 * closed; omitting it is the default and returns the active roster. The flag is
 * only ever sent when it is on — an explicit `include_ended=false` would be the
 * same request with a longer URL, and a stray `undefined` in the query is what
 * makes a "no filter" call look different from a filtered one to anything
 * inspecting it.
 */
export const listMyPatients = (params?: { offset?: number; limit?: number; includeEnded?: boolean }) =>
  doctors()
    .request<Paginated<DoctorPatient>>({
      path: '/doctor/patients',
      method: 'GET',
      query: {
        offset: params?.offset,
        limit: params?.limit,
        ...(params?.includeEnded ? { include_ended: true } : {}),
      },
      format: 'json',
    })
    .then((r) => r.data);

export const listMyThresholds = () =>
  doctors()
    .request<DoctorThreshold[]>({ path: '/doctor/thresholds', method: 'GET', format: 'json' })
    .then((r) => r.data);

export const upsertMyThreshold = (body: DoctorThresholdUpsert) =>
  doctors()
    .request<DoctorThreshold>({
      path: '/doctor/thresholds',
      method: 'PUT',
      body,
      type: ContentType.Json,
      format: 'json',
    })
    .then((r) => r.data);

/**
 * Measurements outside the doctor's bounds.
 *
 * Derived server-side from the vitals on each visit, so raising a bound clears
 * the matching rows on the next fetch. Nothing is acknowledged or stored, which
 * is why there is no mutation counterpart here.
 */
export const listMyThresholdBreaches = (params?: { days?: number; limit?: number }) =>
  doctors()
    .request<ThresholdBreach[]>({
      path: '/doctor/threshold-breaches',
      method: 'GET',
      query: params,
      format: 'json',
    })
    .then((r) => r.data);

// ── Visit-service, under /doctor ──────────────────────────────────────────────
// Same `request()` rule as above. These live on visit-service, not doctors-api,
// because they are about a visit.
//
// The `/doctor` prefix is not decoration: the backend reads the persona off the
// route, so a call under it runs as `doc` for any token holding that role. It is
// what lets one account be a caregiver everywhere else and a doctor here, and it
// is why the doctor's screens must never reach for the unprefixed path.

const visits = () => ApiClient.visitsApi;

/**
 * The doctor's visit list and one visit — the same handlers the staff list uses,
 * aliased under /doctor so the request resolves to the doctor persona and
 * visits_select_policy narrows the rows to their own patients.
 *
 * Hand-wired for the same reason as the rest of this file; the query is passed
 * through as-is, so it takes whatever `listVisits` takes.
 */
export const listDoctorVisits = (query: Record<string, unknown>) =>
  visits()
    .request<VisitsGetAllPaginationResponse>({
      path: '/doctor/visits',
      method: 'GET',
      query,
      format: 'json',
    })
    .then((r) => r.data);

export const getDoctorVisitById = (visitId: number) =>
  visits()
    .request<VisitPopulated>({ path: `/doctor/visits/${visitId}`, method: 'GET', format: 'json' })
    .then((r) => r.data);

export const listDoctorInstructions = (params: { visitId?: number; status?: DoctorInstructionStatus }) =>
  visits()
    .request<Paginated<DoctorInstruction>>({
      path: '/doctor/instructions',
      method: 'GET',
      query: { visit_id: params.visitId, status: params.status },
      format: 'json',
    })
    .then((r) => r.data);

export const createDoctorInstruction = (body: DoctorInstructionCreate) =>
  visits()
    .request<DoctorInstruction>({
      path: '/doctor/instructions',
      method: 'POST',
      body,
      type: ContentType.Json,
      format: 'json',
    })
    .then((r) => r.data);

/**
 * Every instruction, whoever sent it — the office's queue.
 *
 * Unprefixed for the same reason `decideDoctorInstruction` below is: under `/doctor`
 * the request would run as the doctor persona and RLS would narrow the rows to
 * that doctor's own instructions, which is the opposite of a queue.
 */
// `remember` sees the exact query sent, so the queue page can export the same rows.
export const listDoctorInstructionsForOffice = (
  params: {
    status?: DoctorInstructionStatus;
    visitId?: number;
    offset?: number;
    limit?: number;
  },
  remember: <T extends Record<string, unknown>>(query: T) => T = (query) => query
) =>
  visits()
    .request<Paginated<DoctorInstruction>>({
      path: '/doctor-instructions',
      method: 'GET',
      query: remember({
        status: params.status,
        visit_id: params.visitId,
        offset: params.offset,
        limit: params.limit,
      }),
      format: 'json',
    })
    .then((r) => r.data);

/**
 * Office-only; a doctor cannot decide their own instruction.
 *
 * Deliberately NOT under /doctor — this is the one call here a doctor does not
 * make. Under that prefix an account holding both roles would act as a doctor
 * and be refused by the office-only policy behind it.
 */
export const decideDoctorInstruction = (
  instructionId: number,
  body: { status: 'HANDLED' | 'NOT_HANDLED'; decisionNote?: string }
) =>
  visits()
    .request<DoctorInstruction>({
      path: `/doctor-instructions/${instructionId}/decision`,
      method: 'PUT',
      body,
      type: ContentType.Json,
      format: 'json',
    })
    .then((r) => r.data);
